import { makeAutoObservable, runInAction } from 'mobx'
import { api } from '@/shared/api/endpoints'
import type { Currency, WalletResponse } from '@/shared/api/types'
import { explain } from '@/shared/lib/errors'
import type { CabinetStore } from '@/stores/CabinetStore'
import type { UiStore } from '@/stores/UiStore'

/** Что сейчас открыто поверх списка кошельков. */
export type WalletDialog =
  | { kind: 'none' }
  | { kind: 'add' }
  | { kind: 'edit'; wallet: WalletResponse }
  | { kind: 'remove'; wallet: WalletResponse }

/** Черновик формы. Валюта и адрес — строки, а не типы: их ещё не проверили. */
export interface WalletDraft {
  currency: Currency | null
  address: string
  name: string
}

const EMPTY: WalletDraft = { currency: null, address: '', name: '' }

/**
 * Кошельки для вывода.
 *
 * Список живёт в CabinetStore (его же читает обзор), здесь — только
 * состояние диалогов и изменения. Разделение нужно, чтобы после
 * добавления кошелька обзор не показывал старый список: перезагружается
 * один и тот же ресурс, а не две независимые копии.
 */
export class WalletsStore {
  dialog: WalletDialog = { kind: 'none' }
  draft: WalletDraft = EMPTY
  errors: Partial<Record<keyof WalletDraft, string>> = {}
  busy = false

  private readonly cabinet: CabinetStore
  private readonly ui: UiStore

  constructor(cabinet: CabinetStore, ui: UiStore) {
    this.cabinet = cabinet
    this.ui = ui
    makeAutoObservable<this, 'cabinet' | 'ui'>(this, { cabinet: false, ui: false })
  }

  get items(): WalletResponse[] {
    return this.cabinet.wallets.value ?? []
  }

  /**
   * Сколько правил автовывода ссылается на кошелёк.
   *
   * Бекенд удалять такой кошелёк ОТКАЗЫВАЕТСЯ (`wallet_in_use`), и знать
   * об этом надо до нажатия «Удалить», а не после отказа: диалог раньше
   * обещал, что автовыводы просто перестанут работать, — обещание,
   * которого никто не выполнял.
   */
  rulesFor(walletId: string): number {
    const rules = this.cabinet.rules.value ?? []
    return rules.filter((rule) => rule.wallet_id === walletId).length
  }

  openAdd(): void {
    this.dialog = { kind: 'add' }
    this.draft = { ...EMPTY }
    this.errors = {}
  }

  /**
   * Правка. Меняется только название: бекенд принимает на `PATCH`
   * одно поле `name`, и это правильно — смена адреса у существующего
   * кошелька молча увела бы следующий автовывод на новый адрес,
   * а в истории остались бы записи со старым.
   */
  openEdit(wallet: WalletResponse): void {
    this.dialog = { kind: 'edit', wallet }
    this.draft = { currency: wallet.currency, address: wallet.address, name: wallet.name }
    this.errors = {}
  }

  openRemove(wallet: WalletResponse): void {
    this.dialog = { kind: 'remove', wallet }
    // Список правил мог устареть: без свежего диалог покажет
    // «удаление разрешено» там, где бекенд откажет.
    void this.cabinet.rules.load()
  }

  close(): void {
    if (this.busy) return
    this.dialog = { kind: 'none' }
    this.errors = {}
  }

  patch(part: Partial<WalletDraft>): void {
    this.draft = { ...this.draft, ...part }
    for (const key of Object.keys(part) as (keyof WalletDraft)[]) {
      if (this.errors[key]) {
        const next = { ...this.errors }
        delete next[key]
        this.errors = next
      }
    }
  }

  async submit(): Promise<void> {
    if (this.busy) return
    const dialog = this.dialog
    if (dialog.kind === 'add') return this.create()
    if (dialog.kind === 'edit') return this.rename(dialog.wallet)
  }

  private validate(withAddress: boolean): boolean {
    const errors: Partial<Record<keyof WalletDraft, string>> = {}
    if (this.draft.currency === null) errors.currency = 'Выберите валюту'
    const name = this.draft.name.trim()
    if (name === '') errors.name = 'Укажите название'
    else if (name.length > 64) errors.name = 'Не длиннее 64 символов'
    if (withAddress) {
      const address = this.draft.address.trim()
      if (address === '') errors.address = 'Укажите адрес'
      // Пробел внутри адреса — почти всегда след копирования из чата
      // или из PDF. Отправка на такой адрес не возвращается.
      else if (/\s/.test(address)) errors.address = 'В адресе не должно быть пробелов'
    }
    this.errors = errors
    return Object.keys(errors).length === 0
  }

  private async create(): Promise<void> {
    if (!this.validate(true)) return
    const currency = this.draft.currency
    if (currency === null) return
    this.busy = true
    try {
      await api.money.addWallet({
        currency,
        address: this.draft.address.trim(),
        name: this.draft.name.trim(),
      })
      await this.cabinet.wallets.load()
      runInAction(() => { this.dialog = { kind: 'none' } })
      this.ui.success('Кошелёк добавлен')
    } catch (cause) {
      this.fail(cause)
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  private async rename(wallet: WalletResponse): Promise<void> {
    if (!this.validate(false)) return
    const name = this.draft.name.trim()
    if (name === wallet.name) { this.dialog = { kind: 'none' }; return }
    this.busy = true
    try {
      await api.money.updateWallet(wallet.id, { name })
      await this.cabinet.wallets.load()
      runInAction(() => { this.dialog = { kind: 'none' } })
      this.ui.success('Название изменено')
    } catch (cause) {
      this.fail(cause)
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  async remove(): Promise<void> {
    const dialog = this.dialog
    if (dialog.kind !== 'remove' || this.busy) return
    this.busy = true
    try {
      await api.money.deleteWallet(dialog.wallet.id)
      // Автовыводы ссылаются на кошелёк: удалённый мог отключить
      // правило на бекенде, и список правил надо перечитать, иначе
      // человек будет уверен, что вывод по расписанию всё ещё работает.
      await Promise.all([this.cabinet.wallets.load(), this.cabinet.rules.load()])
      runInAction(() => { this.dialog = { kind: 'none' } })
      this.ui.success('Кошелёк удалён')
    } catch (cause) {
      this.fail(cause)
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  private fail(cause: unknown): void {
    this.ui.failure(explain(cause))
  }
}
